import { Component } from '@theme/component';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * Keeps the feature cards on the selected variant. A variant change re-renders only the variant picker, so the cards
 * are taken from the section it fetched — a variant with its own features shows them, any other the product's.
 *
 * @extends {Component}
 */
class ProductFeatures extends Component {
  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    this.closest('.shopify-section')?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, {
      signal: this.#abortController.signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
  }

  /**
   * Re-renders the features for the selected variant.
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        // A morphed element, such as `<main>` for a combined listing sibling, already carries the new features.
        if (!detail?.html || detail.morphElementSelector) return;

        const newFeatures = detail.html.querySelector(`product-features[data-block-id="${this.dataset.blockId}"]`);
        if (!newFeatures || newFeatures.dataset.featuresSource === this.dataset.featuresSource) return;

        // Replaced rather than morphed: the slideshow sets itself up only when it connects, and it
        // measures its width then, so this element is shown first.
        this.hidden = newFeatures.hidden;
        this.dataset.featuresSource = newFeatures.dataset.featuresSource;
        this.replaceChildren(...newFeatures.childNodes);
      })
      .catch((error) => {
        if (error?.name === 'AbortError') return;

        console.warn('[product-features] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('product-features')) {
  customElements.define('product-features', ProductFeatures);
}
